import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  IconButton,
  Spinner,
  Tag,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { EntityTag, tagsApi } from '@texas/api/endpoints/tagsApi';
import { EntityType } from '@texas/api/endpoints/trackerApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { ErrorDetails } from '../shared/alert/ErrorDetails';
import { Icons } from '../shared/Icons';
import { CreateTagModal } from './CreateTagModal';
import { UpdateTagModal } from './UpdateTagModal';
import { ArchiveTagModal } from './ArchiveTagModal';
import { ArticlesOverviewModal } from '../widgets/shared/components/articles/ArticlesOverviewModal';
import { BodyOverride } from '../shared/BodyOverride';
import { useActiveSession } from '@texas/hooks/useSession';
import { hasRole } from '@texas/utils/helpers/roleHelpers';
import { Role } from '@texas/api/endpoints/userApi';
import { TableColumns } from '../dashboard/TableColumns';
import { emptyVariantsSearchQuery } from '@texas/api/endpoints/search/searchVariantsTypes';
import { compareEnum, convertToEnum } from '@texas/utils/helpers/enumHelpers';
import { FilterOptionType } from '../filter/types/types';
import { entityTypeFriendlyName } from '../shared/tracker/translations';
import { TexasHeader } from '../shared/TexasHeader';
import { TexasSwitch } from '../shared/form/TexasSwitch';

export function Tags() {
  const { t } = useTranslation();
  const [selectedTagId, setSelectedTagId] = useState<number | null>(null);
  const [tagToArchive, setTagToArchive] = useState<EntityTag | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);

  const {
    isOpen: isCreateOpen,
    onClose: onCreateClose,
    onOpen: onCreateOpen,
  } = useDisclosure();

  const {
    isOpen: isUpdateOpen,
    onClose: onUpdateClose,
    onOpen: onUpdateOpen,
  } = useDisclosure();

  const {
    isOpen: isArchiveOpen,
    onClose: onArchiveClose,
    onOpen: onArchiveOpen,
  } = useDisclosure();

  const {
    value: selectedTagIdForArticles,
    isOpen: isArticlesOverviewOpen,
    onClose: onArticlesOverviewClose,
    onOpen: onArticlesOverviewOpen,
  } = useValueDisclosure<number>();

  const {
    data: tags,
    refetch,
    loading,
    error,
  } = useApiResource(tagsApi.getAll);

  useEffect(() => {
    refetch({ includeArchived });
  }, [refetch, includeArchived]);

  const handleTagCreated = () => {
    refetch({ includeArchived });
  };

  const handleTagUpdated = () => {
    refetch({ includeArchived });
  };

  const handleEditClick = (tagId: number) => {
    setSelectedTagId(tagId);
    onUpdateOpen();
  };

  const handleArchiveClick = (tag: EntityTag) => {
    setTagToArchive(tag);
    onArchiveOpen();
  };

  const handleTagArchived = () => {
    refetch({ includeArchived });
    onArchiveClose();
  };

  const { request: restoreTag, loading: restoring } = useApiRequest(
    tagsApi.restore,
  );

  const handleRestoreClick = async (tagId: number) => {
    const response = await restoreTag(tagId);
    if (!response.hasError) {
      refetch({ includeArchived });
    }
  };

  const handleArticleCountClick = (tagId: number) => {
    onArticlesOverviewOpen(tagId);
  };

  return (
    <BodyOverride>
      <Card variant="dark">
        <CardBody>
          <Flex gap={2} direction="column">
            <Flex justifyContent="space-between" alignItems="center">
              <Flex gap={4} flexDir="column">
                <TexasHeader text={t('tags.title')} />
                <Flex gap={4} alignItems="center">
                  <Flex alignItems="center" gap={2}>
                    <TexasSwitch
                      label={t('general.includeArchived')}
                      checked={includeArchived}
                      onChange={(checked) => setIncludeArchived(checked)}
                    />
                  </Flex>
                  {loading && <Spinner size="sm" />}
                </Flex>
              </Flex>
              <Button
                leftIcon={<Icons.Plus />}
                variant="texas-solid"
                onClick={onCreateOpen}
              >
                {t('tags.createNew')}
              </Button>
            </Flex>

            {error && <ErrorDetails error={error} />}

            {tags && (
              <VStack align="stretch" spacing={4}>
                {tags.length === 0 ? (
                  <Text color="gray.500">{t('tags.noTags')}</Text>
                ) : (
                  <GroupedTags
                    tags={tags}
                    onEdit={handleEditClick}
                    onArchive={handleArchiveClick}
                    onRestore={handleRestoreClick}
                    onArticleCountClick={handleArticleCountClick}
                    restoring={restoring}
                  />
                )}
              </VStack>
            )}
          </Flex>
        </CardBody>
      </Card>

      <CreateTagModal
        isOpen={isCreateOpen}
        onClose={onCreateClose}
        onTagCreated={handleTagCreated}
      />

      {selectedTagId && (
        <UpdateTagModal
          isOpen={isUpdateOpen}
          onClose={onUpdateClose}
          tagId={selectedTagId}
          onTagUpdated={handleTagUpdated}
        />
      )}

      {tagToArchive && (
        <ArchiveTagModal
          isOpen={isArchiveOpen}
          onClose={onArchiveClose}
          tag={tagToArchive}
          onArchived={handleTagArchived}
        />
      )}

      {selectedTagIdForArticles && (
        <ArticlesOverviewModal
          isOpen={isArticlesOverviewOpen}
          onClose={onArticlesOverviewClose}
          filters={{
            ...emptyVariantsSearchQuery,
            tagIds: [selectedTagIdForArticles],
          }}
          articleSearchProps={{
            filters: [FilterOptionType.Tags],
            searchParams: {
              ...emptyVariantsSearchQuery,
              tagIds: [selectedTagIdForArticles],
            },
          }}
          columns={TableColumns}
        />
      )}
    </BodyOverride>
  );
}

function GroupedTags({
  tags,
  onEdit,
  onArchive,
  onRestore,
  onArticleCountClick,
  restoring,
}: {
  tags: EntityTag[];
  onEdit: (tagId: number) => void;
  onArchive: (tag: EntityTag) => void;
  onRestore: (tagId: number) => Promise<void>;
  onArticleCountClick: (tagId: number) => void;
  restoring: boolean;
}) {
  const { t } = useTranslation();
  const { currentUser } = useActiveSession();

  const systemTags = tags.filter((tag) => tag.systemTag);
  const sharedTags = tags.filter(
    (tag) => tag.userId === null && !tag.systemTag,
  );
  const userTags = tags.filter((tag) => tag.userId !== null && !tag.systemTag);

  return (
    <>
      {systemTags.length > 0 && (
        <TagGroup
          title={t('tags.systemTags')}
          tags={systemTags}
          onEdit={onEdit}
          onArchive={onArchive}
          onRestore={onRestore}
          onArticleCountClick={onArticleCountClick}
          restoring={restoring}
          disableEdit={true}
        />
      )}
      {sharedTags.length > 0 && (
        <TagGroup
          title={t('tags.sharedTags')}
          tags={sharedTags}
          onEdit={onEdit}
          onArchive={onArchive}
          onRestore={onRestore}
          onArticleCountClick={onArticleCountClick}
          restoring={restoring}
          disableEdit={!hasRole(currentUser.role, [Role.SystemAdministrator])}
        />
      )}
      {userTags.length > 0 && (
        <TagGroup
          title={t('tags.myTags')}
          tags={userTags}
          onEdit={onEdit}
          onArchive={onArchive}
          onRestore={onRestore}
          onArticleCountClick={onArticleCountClick}
          restoring={restoring}
        />
      )}
    </>
  );
}

function TagGroup({
  title,
  tags,
  onEdit,
  onArchive,
  onRestore,
  onArticleCountClick,
  restoring,
  disableEdit,
}: {
  title: string;
  tags: EntityTag[];
  onEdit: (tagId: number) => void;
  onArchive: (tag: EntityTag) => void;
  onRestore: (tagId: number) => Promise<void>;
  onArticleCountClick: (tagId: number) => void;
  restoring: boolean;
  disableEdit?: boolean;
}) {
  return (
    <VStack align="stretch" spacing={2}>
      <Text variant="main">{title}</Text>
      {tags.map((tag) => (
        <TagRow
          key={tag.id}
          tag={tag}
          onEdit={() => onEdit(tag.id)}
          onArchive={() => onArchive(tag)}
          onRestore={() => onRestore(tag.id)}
          onArticleCountClick={() => onArticleCountClick(tag.id)}
          restoring={restoring}
          disableEdit={disableEdit}
        />
      ))}
    </VStack>
  );
}

function TagRow({
  tag,
  onEdit,
  onArchive,
  onRestore,
  onArticleCountClick,
  restoring,
  disableEdit,
}: {
  tag: EntityTag;
  onEdit: () => void;
  onArchive: () => void;
  onRestore: () => Promise<void>;
  onArticleCountClick: () => void;
  restoring: boolean;
  disableEdit?: boolean;
}) {
  const { t } = useTranslation();

  const isSharedTag = tag.userId === null && !tag.systemTag;

  const entityCountsArray = Object.entries(tag.entityCounts)
    .map(([type, count]) => ({
      type,
      count,
      label: entityTypeFriendlyName(t, type as unknown as EntityType),
    }))
    .filter((item) => item.count > 0)
    .sort((a, b) => b.count - a.count);

  const applicableTypes =
    isSharedTag &&
    tag.applicableEntityTypes != null &&
    tag.applicableEntityTypes.length > 0
      ? tag.applicableEntityTypes.map((et) => entityTypeFriendlyName(t, et))
      : null;

  return (
    <Box
      role="group"
      p={3}
      borderRadius="md"
      bg="texas.bg.800"
      _hover={{ bg: 'texas.bg.700' }}
      _light={{
        bg: 'gray.100',
        _hover: {
          bg: 'gray.50',
        },
      }}
      opacity={tag.archived ? 0.6 : 1}
    >
      <Flex justifyContent="space-between" alignItems="center">
        <Flex gap={3} alignItems="center" flex={1}>
          <Flex direction="column" gap={1} flex={1}>
            <Flex gap={3} alignItems="center">
              <Tag size="lg" bg={tag.color} color="white" fontWeight="bold">
                {tag.label}
              </Tag>
              {tag.archived && (
                <Badge colorScheme="gray" fontSize="xs">
                  {t('tags.archived')}
                </Badge>
              )}
              {isSharedTag && applicableTypes !== null && (
                <Flex gap={1} alignItems="center" flexWrap="wrap">
                  {applicableTypes.map((label) => (
                    <Badge
                      key={label}
                      variant="subtle"
                      colorScheme="blue"
                      fontSize="xs"
                    >
                      {label}
                    </Badge>
                  ))}
                </Flex>
              )}

              <Flex gap={2} alignItems="center" flexWrap="wrap">
                {entityCountsArray.map((item) => {
                  const type = convertToEnum(EntityType, item.type);

                  if (!compareEnum(EntityType, type, EntityType.Article)) {
                    return (
                      <Text key={item.type} fontSize="xs">
                        {item.count} {item.label}
                      </Text>
                    );
                  }
                  return (
                    <Button
                      key={item.type}
                      fontSize="xs"
                      variant="link"
                      onClick={onArticleCountClick}
                    >
                      {item.count} {item.label}
                    </Button>
                  );
                })}
              </Flex>
            </Flex>
          </Flex>
        </Flex>
        {!disableEdit && (
          <Flex
            gap={2}
            opacity={0}
            _groupHover={{ opacity: 1 }}
            transition="opacity 0.2s"
          >
            {!tag.archived && (
              <>
                <Tooltip label={t('general.edit')}>
                  <IconButton
                    aria-label={t('general.edit')}
                    icon={<Icons.Pencil />}
                    size="sm"
                    variant="ghost"
                    onClick={onEdit}
                  />
                </Tooltip>
                <Tooltip label={t('tags.archive')}>
                  <IconButton
                    aria-label={t('tags.archive')}
                    icon={<Icons.Archive />}
                    size="sm"
                    variant="ghost"
                    onClick={onArchive}
                  />
                </Tooltip>
              </>
            )}
            {tag.archived && (
              <Tooltip label={t('tags.restore')}>
                <IconButton
                  aria-label={t('tags.restore')}
                  icon={<Icons.Undo />}
                  size="sm"
                  variant="ghost"
                  onClick={onRestore}
                  isLoading={restoring}
                />
              </Tooltip>
            )}
          </Flex>
        )}
      </Flex>
    </Box>
  );
}
